"use client"
import React, { useEffect, useRef, useState } from 'react'
import { gsap } from 'gsap'
import { cn } from '@/shared/utils/tw'
import { useModal } from '@/shared/providers/ModalProvider'
import Image from 'next/image'
import { useTranslation } from 'react-i18next'
import { changeLanguage } from '@/shared/lib/i18n'

const NavigationMenu = () => {
  const { isModalOpen, closeModal } = useModal()
  const modalRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const { t, i18n } = useTranslation()
  const [isClient, setIsClient] = useState(false)
  const [currentLanguage, setCurrentLanguage] = useState('ko')

  useEffect(() => {
    setCurrentLanguage(i18n?.language || 'ko')
  }, [i18n?.language])

  // 클라이언트 마운트 후에만 렌더링
  useEffect(() => {
    setIsClient(true)
  }, [])

  useEffect(() => {
    if (isModalOpen) {
      // 모달이 열릴 때 fade in 애니메이션
      gsap.set(modalRef.current, { display: 'flex' })
      gsap.fromTo(
        modalRef.current,
        { opacity: 0 },
        { opacity: 1, duration: 0.3, ease: 'power1.out' }
      )
    } else {
      // 모달이 닫힐 때 fade out 애니메이션
      gsap.to(modalRef.current, {
        opacity: 0,
        duration: 0.3,
        ease: 'power1.out',
        onComplete: () => {
          gsap.set(modalRef.current, { display: 'none' })
        }
      })
    }
  }, [isModalOpen])

  // 서버 렌더링 시에는 아무것도 렌더링하지 않음
  if (!isClient) {
    return null
  }

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      closeModal()
    }
  }

  return (
    <div
      ref={modalRef}
      className={cn(
        "fixed inset-0 z-50 flex items-center justify-center",
        "backdrop-blur-sm",
        "hidden" // 초기에는 숨김
      )}
      onClick={handleOverlayClick}
    >
      {/* 배경 오버레이 */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />

      {/* 모달 컨텐츠 */}
      <div
        ref={contentRef}
        className={cn(
          "relative w-full h-full flex flex-col justify-between",
          "text-white pb-[17px] pt-[63px]" // Nav 높이만큼 상단 패딩 추가
        )}
      >
        {/* 그라데이션 보더 */}

        <div className='pr-[28px] flex flex-col'>
          <Line className='mt-[34px]' />
          {/* 메뉴 아이템들 */}
          <div className="flex-1 flex flex-col justify-start items-end gap-y-[30px]">
            <MenuItem
              text={t('modal.menu.faq')}
              href="https://goodganglabs.notion.site/Good-Gang-Labs-FAQ-2293837bc7048029afaef2bb67adf0ed"
            />
            <MenuItem
              text={t('modal.menu.contact')}
              href="mailto:hello@goodganglabs.com"
            />
            <MenuItem
              text={t('modal.menu.policies')}
              href={currentLanguage === 'ko'
                ? "https://goodganglabs.notion.site/Good-Gang-Labs-Polices-KOR-2293837bc70480fb9b56cfd76b77ab17"
                : "https://goodganglabs.notion.site/Good-Gang-Labs-Polices-EN-2253837bc70480cea65dfee8eb2327e6"
              }
            />
          </div>
          <Line className='my-[28px]' />

          {/* 언어 선택 */}
          <Languages currentLanguage={currentLanguage} setCurrentLanguage={setCurrentLanguage} />
        </div>
        {/* Footer */}
        <footer className="pr-[18px] flex flex-col items-end gap-y-[3px]">
          <a
            href="https://www.linkedin.com/company/goodgang-labs/posts/?feedView=all"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Image
              className={cn("w-[44px] h-[44px]")}
              src="/svg/linked-in.svg"
              width={32}
              height={32}
              alt="LinkedIn"
              draggable={false}
              priority
            />
          </a>
          <p className="text-[#E8E8E8]/90 text-[10px] leading-[12.5px] font-normal text-end font-bai-jamjuree pr-[8px]">
            © 2025 Good Gang Labs Pte. Ltd. All rights reserved.
          </p>
        </footer>
      </div>
    </div>
  )
}

const Line = ({ className }: { className?: string }) => {
  return (
    <div
      className={cn("w-[171px] h-[0.8px] mb-[28px] self-end", className)}
      style={{
        background: 'linear-gradient(to right, rgba(255,255,255,0.08) 0%, rgba(255,255,255,1) 52.91%, rgba(255,255,255,0.08) 100%)'
      }}
    />
  )
}

const Languages = ({ currentLanguage, setCurrentLanguage }: { currentLanguage: string; setCurrentLanguage: (lang: string) => void }) => {

  const handleLanguageChange = async (lng: 'ko' | 'en') => {
    await changeLanguage(lng)
    setCurrentLanguage(lng)
  }

  const languages = [
    { code: 'ko' as const, label: '한국어' },
    { code: 'en' as const, label: 'English' }
  ]

  return (
    <div className="flex justify-end items-center gap-x-[30px]">
      {languages.map(({ code, label }) => (
        <button
          key={code}
          onClick={() => handleLanguageChange(code)}
          className={cn(
            "font-bold text-[17px] leading-normal font-pretendard transition-colors duration-200 ease-in-out",
            currentLanguage === code ? "text-white" : "text-white/60"
          )}
        >
          {label}
        </button>
      ))}
    </div>
  )
}

const MenuItem = ({ text, href }: { text: string; href?: string }) => {
  const handleClick = () => {
    if (href) {
      if (href.startsWith('mailto:')) {
        window.location.href = href
      } else {
        window.open(href, '_blank', 'noopener,noreferrer')
      }
    }
  }

  return (
    <div className="text-right">
      <span
        className={cn(
          "text-[#F7F7F7] text-[32px] leading-[142%] font-semibold font-pretendard",
          href && "cursor-pointer hover:text-white/80 transition-colors duration-200"
        )}
        onClick={handleClick}
      >
        {text}
      </span>
    </div>
  )
}

export default NavigationMenu